import { useEffect, useRef } from 'react';
import { useAdSense } from '../hooks/useAdSense';

/**
 * AdSense 광고 슬롯 컴포넌트.
 *
 * @param {string} position - 'header' | 'sidebar' | 'footer' | 'content'
 * @param {object} style    - 추가 인라인 스타일
 *
 * 동작:
 *  - 설정 로드 전(null): 빈 div 렌더링 (레이아웃 안정)
 *  - enabled=false 또는 해당 슬롯 숨김(visible=false): 렌더링 안 함
 *  - enabled=true + slotId 있음 + visible=true: AdSense 스크립트 삽입 + <ins> 렌더링
 */
export default function AdSenseSlot({ position, style = {} }) {
  const config = useAdSense();
  const insRef = useRef(null);
  const pushed = useRef(false);

  const slotId = config?.[`${position}SlotId`];
  // 슬롯별 표시 여부 (필드가 없는 기존 데이터는 true로 간주)
  const isVisible = config?.[`${position}Visible`] !== false;
  const isActive = config?.enabled && slotId && isVisible;

  useEffect(() => {
    if (!isActive || pushed.current) return;

    // AdSense 글로벌 스크립트를 <head>에 한 번만 삽입
    if (!document.getElementById('adsense-script')) {
      const script = document.createElement('script');
      script.id = 'adsense-script';
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.publisherId}`;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    // <ins> 요소가 DOM에 마운트된 후 광고 push
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      // AdSense 미로드 상태에서는 무시
    }
  }, [isActive, config]);

  // 설정 로딩 중이거나 비활성/슬롯 미설정/숨김 상태: 렌더링 안 함
  if (config === null || !isVisible || !isActive) {
    return null;
  }

  // 활성화: 실제 AdSense ins 요소
  return (
    <ins
      ref={insRef}
      className="adsbygoogle"
      style={{ display: 'block', ...style }}
      data-ad-client={config.publisherId}
      data-ad-slot={slotId}
      data-ad-format="auto"
      data-full-width-responsive="true"
    />
  );
}
